/**
 * 강사(tutor) 인라인 편집 / 폼 공용 유효성 스펙
 *
 * 원본 스펙: 대량등록 템플릿 "강사정보_예시_v1.8"의 데이터 유효성(드롭다운/포맷) 규칙.
 * ag-grid 셀 에디터와 antd Form 양쪽에서 동일 규칙을 쓰기 위한 단일 소스.
 *
 * - 순수 검증 함수는 (value, row) => '' | '에러메시지' 형태(빈 문자열 = 통과)로 통일한다.
 *   → ag-grid valueSetter, antd rules(validator) 어디서든 래핑해서 사용.
 */
import { checkPhoneNumber } from 'utils/utilCommon'

/* ------------------------------ 허용값(드롭다운) ------------------------------ */
export const TUTOR_OPTIONS = {
    isEmployment: ['신규', '재고용'], // 저장 시 boolean(재고용=true)
    yn: ['Y', 'N'], // boolean 컬럼 표시용
    advancedInstructor: ['초급강사', '중급강사', '고급강사'],
    gender: ['남', '여'],
    employmentType: ['직접채용', '위촉'],
    educationLevel: ['고졸', '전문대졸', '대졸', '석사', '박사'],
    privateCertification: ['1급', '2급', '3급', '없음'],
    registrationYear: ['2026', '2025', '2024', '2023', '2022', '2021', '2020', '2019'],
}

/* ------------------------------ 순수 검증 프리미티브 ------------------------------ */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const isBlank = v => v === undefined || v === null || String(v).trim() === ''

// 2자 이상, 순수 숫자가 아님 (성명/주소/전공/기관명 등) — 엑셀 custom 규칙과 동일
export const isText2 = v => !isBlank(v) && String(v).trim().length >= 2 && Number.isNaN(Number(String(v).trim()))

// 생년월일 6자리 + 달력 유효성
export const isBirth6 = v => {
    if (isBlank(v) || !/^\d{6}$/.test(String(v))) return false
    const s = String(v)
    const yy = parseInt(s.slice(0, 2), 10)
    const mm = parseInt(s.slice(2, 4), 10)
    const dd = parseInt(s.slice(4, 6), 10)
    const fullYear = yy >= 0 && yy <= 25 ? 2000 + yy : 1900 + yy
    const d = new Date(fullYear, mm - 1, dd)
    return d.getFullYear() === fullYear && d.getMonth() === mm - 1 && d.getDate() === dd
}

export const isEmail = v => !isBlank(v) && EMAIL_REGEX.test(String(v))

// YYYY-MM-DD 형식 + 실제 유효 날짜
export const isYmd = v => {
    if (isBlank(v) || !/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return false
    const [y, m, d] = String(v).split('-').map(n => parseInt(n, 10))
    const dt = new Date(y, m - 1, d)
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
}

const yearOf = v => (isYmd(v) ? parseInt(String(v).slice(0, 4), 10) : NaN)

/* ------------------------------ 필드 스펙 ------------------------------
 * key         : 백엔드/그리드 필드명
 * label       : 사용자 표시 라벨
 * required    : 필수 여부
 * editor      : 'select' | 'boolean' | 'text'
 * options     : select 허용값
 * validate    : (value, row) => '' | '에러메시지'
 * boolean 컬럼은 ag-grid 기본 boolean 에디터(체크박스)를 쓰므로 validate 불필요.
 */
export const TUTOR_FIELD_SPEC = {
    isEmployment: {
        label: '고용 여부',
        required: true,
        editor: 'select',
        options: TUTOR_OPTIONS.isEmployment,
        // 그리드는 boolean(재고용=true), 폼은 문자열('신규'/'재고용') — 양쪽 허용
        validate: v =>
            typeof v === 'boolean' || TUTOR_OPTIONS.isEmployment.includes(v)
                ? ''
                : '고용 여부는 신규/재고용 중 선택하세요.',
    },
    advancedInstructor: {
        label: '강사등급',
        required: true,
        editor: 'select',
        options: TUTOR_OPTIONS.advancedInstructor,
        validate: v => (TUTOR_OPTIONS.advancedInstructor.includes(v) ? '' : '강사등급을 선택하세요.'),
    },
    tutorName: {
        label: '성명',
        required: true,
        editor: 'text',
        validate: v => (isText2(v) ? '' : '성명은 2자 이상, 숫자만은 불가합니다.'),
    },
    gender: {
        label: '성별',
        required: true,
        editor: 'select',
        options: TUTOR_OPTIONS.gender,
        validate: v => (TUTOR_OPTIONS.gender.includes(v) ? '' : '성별은 남/여 중 선택하세요.'),
    },
    residentNum: {
        label: '생년월일',
        required: true,
        editor: 'text',
        validate: v => (isBirth6(v) ? '' : '생년월일은 숫자 6자리(YYMMDD)의 유효한 날짜여야 합니다.'),
    },
    address: {
        label: '주소',
        required: true,
        editor: 'text',
        validate: v => (isText2(v) ? '' : '주소는 2자 이상 입력하세요.'),
    },
    contact: {
        label: '연락처',
        required: true,
        editor: 'text',
        validate: v => (checkPhoneNumber(v) ? '' : '연락처는 010-0000-0000 형식이어야 합니다.'),
    },
    email: {
        label: '이메일',
        required: true,
        editor: 'text',
        validate: v => (isEmail(v) ? '' : '올바른 이메일 형식이 아닙니다.'),
    },
    cohortStartYear: {
        label: '기수(시작년도)',
        required: false,
        editor: 'text',
        validate: v => (isBlank(v) || /^\d{4}$/.test(String(v)) ? '' : '기수는 4자리 연도여야 합니다.'),
    },
    startDate: {
        label: '계약시작일',
        required: true,
        editor: 'text',
        // 활동연도(registrationYear)와 연도 일치
        validate: (v, row) => {
            if (!isYmd(v)) return '계약시작일 형식(YYYY-MM-DD)이 올바르지 않습니다.'
            const ry = Number(row?.registrationYear)
            if (ry && yearOf(v) !== ry) return `계약시작일은 활동연도(${ry})와 같아야 합니다.`
            return ''
        },
    },
    endDate: {
        label: '계약만료일',
        required: true,
        editor: 'text',
        validate: (v, row) => {
            if (!isYmd(v)) return '계약만료일 형식(YYYY-MM-DD)이 올바르지 않습니다.'
            const ry = Number(row?.registrationYear)
            if (ry && yearOf(v) !== ry) return `계약만료일은 활동연도(${ry})와 같아야 합니다.`
            if (isYmd(row?.startDate) && v < row.startDate) return '계약만료일은 계약시작일 이후여야 합니다.'
            return ''
        },
    },
    employmentType: {
        label: '활동형태',
        required: true,
        editor: 'select',
        options: TUTOR_OPTIONS.employmentType,
        validate: v => (TUTOR_OPTIONS.employmentType.includes(v) ? '' : '활동형태는 직접채용/위촉 중 선택하세요.'),
    },
    registrationYear: {
        label: '활동연도',
        required: true,
        editor: 'select',
        options: TUTOR_OPTIONS.registrationYear,
        validate: v => (TUTOR_OPTIONS.registrationYear.includes(String(v)) ? '' : '활동연도를 선택하세요.'),
    },
    major: {
        label: '전공(학과명)',
        required: true,
        editor: 'text',
        validate: v => (isText2(v) ? '' : '전공(학과명)은 2자 이상 입력하세요.'),
    },
    educationLevel: {
        label: '최종학력',
        required: true,
        editor: 'select',
        options: TUTOR_OPTIONS.educationLevel,
        validate: v => (TUTOR_OPTIONS.educationLevel.includes(v) ? '' : '최종학력을 선택하세요.'),
    },
    privateCertification: {
        label: 'SW·AI 교육강사 자격증',
        required: true,
        editor: 'select',
        options: TUTOR_OPTIONS.privateCertification,
        validate: v => (TUTOR_OPTIONS.privateCertification.includes(v) ? '' : 'SW·AI 자격증을 선택하세요.'),
    },
    languages: {
        label: '사용 가능한 외국어',
        required: false,
        editor: 'text',
        validate: v => (isBlank(v) || isText2(v) ? '' : '외국어는 2자 이상 입력하세요.'),
    },
    // 자격증: 다중값을 콤마로 구분한 자유 입력 (선택). 저장 시 콤마→배열 변환.
    swCertification: {
        label: '자격증',
        required: false,
        editor: 'text',
        validate: () => '',
    },
    areaName: { label: '권역', required: false, editor: 'text', validate: () => '' },
    cooperative: { label: '협동조합', required: false, editor: 'text', validate: () => '' },
    remarks: { label: '비고', required: false, editor: 'text', validate: () => '' },
}

// boolean(Y/N) 편집 컬럼 — ag-grid 기본 boolean 에디터 사용, 별도 validate 없음
export const TUTOR_BOOLEAN_FIELDS = [
    'isYouth',
    'isRetiree',
    'isCareerGapFemale',
    'isForeign',
    'isDisability',
    'isVeterans',
    'isTeacherCertification',
    'isTeachingExperience',
    'isLanguage',
    'isCar',
]

/**
 * 한 필드 검증. 필수/형식을 순서대로 확인.
 * @returns '' (통과) | 에러메시지
 */
export const validateTutorField = (key, value, row) => {
    const spec = TUTOR_FIELD_SPEC[key]
    if (!spec) return ''
    const blank = value === undefined || value === null || String(value).trim() === ''
    if (spec.required && blank) return `${spec.label}은(는) 필수입니다.`
    if (blank) return '' // 선택 필드 미입력은 통과
    return spec.validate ? spec.validate(value, row) : ''
}

/**
 * 행 전체 검증 (행 단위 저장 직전 최종 확인).
 * @returns { valid: boolean, errors: { [key]: message } }
 */
export const validateTutorRow = row => {
    const errors = {}
    Object.keys(TUTOR_FIELD_SPEC).forEach(key => {
        const msg = validateTutorField(key, row?.[key], row)
        if (msg) errors[key] = msg
    })
    return { valid: Object.keys(errors).length === 0, errors }
}
